import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import logo from "../assests/logo/logo.webp";
import registration from "../assests/img/registration.png";
import studentlogin from "../assests/img/studentlogin.png";

const BASE_URL = process.env.REACT_APP_API_URL || "http://192.168.0.57:5001/api";

const MAX_VISIBLE = 7;

function slugify(name = "") {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

// parentPath is the already-built slug path of everything above this item
function pageHref(item, parentPath = "") {
  if (item.page_url) return item.page_url;
  const slug = slugify(item.page_name);
  return parentPath ? `/${parentPath}/${slug}` : `/${slug}`;
}

// ── Google Translate language switch (English / Odia / Hindi) ────────────────
// DEFAULT = English. The visitor's choice is saved in localStorage ("site_lang")
// and index.html sets Google's "googtrans" cookie from it before Google loads.
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "or", label: "ଓଡ଼ିଆ (Odia)" },
  { code: "hi", label: "हिन्दी (Hindi)" },
];

function getCurrentLang() {
  try {
    const saved = localStorage.getItem("site_lang");
    return LANGUAGES.some(l => l.code === saved) ? saved : "en";
  } catch (e) {
    return "en";
  }
}

// Remove Google's translation cookie (host-only and domain variants)
// A cookie is only deleted when name + domain + path all match how it was set,
// so we try every host/parent-domain and every path prefix of the current URL.
function clearGoogTrans() {
  const past = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const host = window.location.hostname;
  const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(host);

  const domains = [undefined, host, "." + host];
  if (!isIp && host !== "localhost") {
    const parts = host.split(".");
    for (let i = 1; i < parts.length - 1; i++) {
      const parent = parts.slice(i).join(".");
      domains.push(parent, "." + parent);
    }
  }

  const paths = ["/"];
  let acc = "";
  window.location.pathname.split("/").filter(Boolean).forEach(seg => {
    acc += "/" + seg;
    paths.push(acc);
  });

  domains.forEach(d => {
    paths.forEach(p => {
      document.cookie = `googtrans=; ${past}; path=${p}` + (d ? `; domain=${d}` : "");
    });
  });
}

// Odia / Hindi: switch WITHOUT reloading by driving Google's hidden language
// dropdown (.goog-te-combo). Falls back to a reload only if the Google widget
// has not finished loading yet.
// English: Google rewrites the page's text nodes, and undoing that in place
// makes React throw removeChild / insertBefore errors. So for English we clear
// the cookie and reload; index.html then loads the original page cleanly.
function setLanguage(code) {
  try {
    localStorage.setItem("site_lang", code);
    sessionStorage.removeItem("lang_reset_done"); // allow the safety reset again
  } catch (e) {}

  if (code === "en") {
    clearGoogTrans();
    window.location.reload();
    return;
  }

  const combo = document.querySelector(".goog-te-combo");
  if (!combo) {
    window.location.reload();
    return;
  }

  combo.value = code;
  combo.dispatchEvent(new Event("change"));
}

function LanguageSwitch({ lang, onChange }) {
  return (
    <div className="lang-switch" translate="no">
      <select
        className="lang-select"
        value={lang}
        onChange={e => onChange(e.target.value)}
        aria-label="Select language"
      >
        {LANGUAGES.map(l => (
          <option key={l.code} value={l.code}>
            {l.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
function NavSkeleton() {
  return (
    <ul className="nav-list desktop-nav" style={{ marginBottom: 0 }}>
      {Array.from({ length: MAX_VISIBLE }).map((_, i) => (
        <li key={i} className="nav-item">
          <span className="nav-skeleton-item skeleton" style={{ width: `${55 + i * 8}px` }} />
        </li>
      ))}
    </ul>
  );
}

// ── Recursive flyout submenu (any depth, level 3+) ────────────────────────────
// IMPORTANT: submenus are ALWAYS rendered in the DOM and only shown/hidden with
// CSS. Google Translate translates what is in the DOM, so a submenu that is
// mounted only on hover would stay in English.
// `open` tells the flyout when it is visible, so it can flip upward if it
// would overflow the bottom of the screen.
function FlyoutSubmenu({ items, parentPath = "", alignLeft = false, onNavigate, open = false }) {
  const ref = useRef(null);
  const [flip, setFlip] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el || !el.parentElement) return;
    const parentTop = el.parentElement.getBoundingClientRect().top;
    setFlip(parentTop + el.offsetHeight > window.innerHeight);
  }, [open]);

  if (!items?.length) return null;

  return (
    <ul
      ref={ref}
      className={`sub-dropdown${alignLeft ? " sub-dropdown-left" : ""}${flip ? " flip-up" : ""}`}
    >
      {items.map(item => {
        const itemPath = parentPath ? `${parentPath}/${slugify(item.page_name)}` : slugify(item.page_name);
        const hasChildren = item.children?.length > 0;
        const isOpen = openId === item.page_id;
        return (
          <li
            key={item.page_id}
            className={`dropdown-item${hasChildren ? " has-sub" : ""}${isOpen ? " sub-open" : ""}`}
            onMouseEnter={() => hasChildren && setOpenId(item.page_id)}
            onMouseLeave={() => setOpenId(null)}
          >
            <Link
              to={pageHref(item, parentPath)}
              className="dropdown-link sub-dropdown-link"
              onClick={onNavigate}
            >
              {item.page_name}
              {hasChildren && <span className="sub-chevron">&rsaquo;</span>}
            </Link>
            {hasChildren && (
              <FlyoutSubmenu
                items={item.children}
                parentPath={itemPath}
                alignLeft={alignLeft}
                onNavigate={onNavigate}
                open={isOpen}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Level-2 dropdown (always in the DOM, shown by CSS when parent is active) ──
function Dropdown({ items, alignRight = false, parentPath = "" }) {
  const [hoveredSub, setHoveredSub] = useState(null);
  if (!items?.length) return null;
  return (
    <ul className={`dropdown${alignRight ? " dropdown-right" : ""}`}>
      {items.map(child => {
        const childPath = parentPath ? `${parentPath}/${slugify(child.page_name)}` : slugify(child.page_name);
        const hasChildren = child.children?.length > 0;
        const isOpen = hoveredSub === child.page_id;
        return (
          <li
            key={child.page_id}
            className={`dropdown-item${hasChildren ? " has-sub" : ""}${isOpen ? " sub-open" : ""}`}
            onMouseEnter={() => hasChildren && setHoveredSub(child.page_id)}
            onMouseLeave={() => setHoveredSub(null)}
          >
            <Link to={pageHref(child, parentPath)} className="dropdown-link">
              {child.page_name}
              {hasChildren ? <span className="sub-chevron">&rsaquo;</span> : null}
            </Link>
            {hasChildren && (
              <FlyoutSubmenu
                items={child.children}
                parentPath={childPath}
                alignLeft={alignRight}
                open={isOpen}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Single desktop nav item ───────────────────────────────────────────────────
function NavItem({ menu, isActive, onEnter, onLeave, alignRight }) {
  const menuSlug = slugify(menu.page_name);
  return (
    <li
      className={`nav-item${menu.children?.length ? " has-dropdown" : ""}${isActive ? " active" : ""}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {menu.children?.length ? (
        <button className="nav-link">
          {menu.page_name}
          <span className="chevron">▾</span>
        </button>
      ) : (
        <Link to={pageHref(menu)} className="nav-link">{menu.page_name}</Link>
      )}
      {menu.children?.length > 0 && (
        <Dropdown items={menu.children} alignRight={alignRight} parentPath={menuSlug} />
      )}
    </li>
  );
}

// ── Recursive mobile sub-tree (any depth), used inside the accordion ─────────
// Always rendered; `show` only toggles visibility (so Google can translate it).
function MobileSubTree({ items, parentPath = "", show = true }) {
  const [openIds, setOpenIds] = useState({});
  const toggle = id => setOpenIds(p => ({ ...p, [id]: !p[id] }));

  if (!items?.length) return null;

  return (
    <ul className="sub-dropdown mobile-sub" style={show ? undefined : { display: "none" }}>
      {items.map(item => {
        const itemPath = parentPath ? `${parentPath}/${slugify(item.page_name)}` : slugify(item.page_name);
        const hasChildren = item.children?.length > 0;
        return (
          <li key={item.page_id}>
            {hasChildren ? (
              <>
                <button className="dropdown-link mobile-sub-toggle" onClick={() => toggle(item.page_id)}>
                  {item.page_name}
                  <span className="sub-chevron">{openIds[item.page_id] ? "▴" : "▾"}</span>
                </button>
                <MobileSubTree
                  items={item.children}
                  parentPath={itemPath}
                  show={!!openIds[item.page_id]}
                />
              </>
            ) : (
              <Link to={pageHref(item, parentPath)} className="dropdown-link sub-dropdown-link">
                {item.page_name}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Mobile accordion ──────────────────────────────────────────────────────────
function MobileMenu({ items }) {
  const [openMenus, setOpenMenus] = useState({});
  const [openSubs, setOpenSubs] = useState({});
  const toggle = (id, setter) => setter(p => ({ ...p, [id]: !p[id] }));

  return (
    <ul className="nav-list open">
      {items.map(menu => {
        const menuSlug = slugify(menu.page_name);
        return (
          <li key={menu.page_id} className={`nav-item${menu.children?.length ? " has-dropdown" : ""}`}>
            {menu.children?.length ? (
              <>
                <button className="nav-link" onClick={() => toggle(menu.page_id, setOpenMenus)}>
                  {menu.page_name}
                  <span className="chevron">{openMenus[menu.page_id] ? "▴" : "▾"}</span>
                </button>
                <ul className="dropdown" style={openMenus[menu.page_id] ? undefined : { display: "none" }}>
                  {menu.children.map(child => {
                    const childPath = `${menuSlug}/${slugify(child.page_name)}`;
                    return (
                      <li key={child.page_id} className={`dropdown-item${child.children?.length ? " has-sub" : ""}`}>
                        {child.children?.length ? (
                          <>
                            <button className="dropdown-link mobile-sub-toggle" onClick={() => toggle(child.page_id, setOpenSubs)}>
                              {child.page_name}
                              <span className="sub-chevron">{openSubs[child.page_id] ? "▴" : "▾"}</span>
                            </button>
                            <MobileSubTree
                              items={child.children}
                              parentPath={childPath}
                              show={!!openSubs[child.page_id]}
                            />
                          </>
                        ) : (
                          <Link to={pageHref(child, menuSlug)} className="dropdown-link">{child.page_name}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <Link to={pageHref(menu)} className="nav-link">{menu.page_name}</Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── "More" overflow dropdown ──────────────────────────────────────────────────
function MoreDropdown({ items, isActive, onEnter, onLeave }) {
  const [activeSub, setActiveSub] = useState(null);

  return (
    <li
      className={`nav-item has-dropdown${isActive ? " active" : ""}`}
      onMouseEnter={onEnter}
      onMouseLeave={() => { onLeave(); setActiveSub(null); }}
    >
      <button className="nav-link">
        More <span className="chevron">{isActive ? "\u25b4" : "\u25be"}</span>
      </button>

      <ul className="dropdown dropdown-right more-dropdown">
        {items.map(menu => {
          const menuSlug = slugify(menu.page_name);
          const hasChildren = menu.children?.length > 0;
          const isOpen = activeSub === menu.page_id;
          return (
            <li
              key={menu.page_id}
              className={`dropdown-item${hasChildren ? " has-sub" : ""}${isOpen ? " sub-open" : ""}`}
              onMouseEnter={() => hasChildren && setActiveSub(menu.page_id)}
              onMouseLeave={() => setActiveSub(null)}
            >
              {hasChildren ? (
                <span className="dropdown-link more-parent">
                  {menu.page_name}
                  <span className="sub-chevron">&#x203a;</span>
                </span>
              ) : (
                <Link to={pageHref(menu)} className="dropdown-link">
                  {menu.page_name}
                </Link>
              )}
              {hasChildren && (
                <FlyoutSubmenu
                  items={menu.children}
                  parentPath={menuSlug}
                  alignLeft
                  open={isOpen}
                />
              )}
            </li>
          );
        })}
      </ul>
    </li>
  );
}

// ── Main Navbar ───────────────────────────────────────────────────────────────
export default function Navbar() {
  const [menuTree, setMenuTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(getCurrentLang);

  const location = useLocation();
  const navRef = useRef(null);

  const visibleMenus = menuTree.slice(0, MAX_VISIBLE);
  const overflowMenus = menuTree.slice(MAX_VISIBLE);

  const handleLanguage = code => {
    setLang(code);
    setLanguage(code);
  };

  // Safety net: the visitor chose English but the page still came up translated
  // (a stale Google cookie survived). Clear it and reload once; the sessionStorage
  // flag stops this from ever looping.
  useEffect(() => {
    if (getCurrentLang() !== "en") return;
    const t = setTimeout(() => {
      const html = document.documentElement;
      const translated =
        html.classList.contains("translated-ltr") || html.classList.contains("translated-rtl");
      let done = false;
      try { done = !!sessionStorage.getItem("lang_reset_done"); } catch (e) {}
      if (translated && !done) {
        try { sessionStorage.setItem("lang_reset_done", "1"); } catch (e) {}
        clearGoogTrans();
        window.location.reload();
      }
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  // Fetch menus (retries up to 3 times if the API fails)
  useEffect(() => {
    const controller = new AbortController();

    const fetchMenus = async (attempt = 1) => {
      try {
        const response = await fetch(`${BASE_URL}/menus`, {
          signal: controller.signal,
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          setMenuTree(data);
        } else {
          console.error("Invalid menu API response:", data);
          setMenuTree([]);
        }

        setLoading(false);
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        console.error(`Menu fetch failed (attempt ${attempt}):`, err.message);

        if (attempt < 3) {
          setTimeout(() => {
            if (!controller.signal.aborted) {
              fetchMenus(attempt + 1);
            }
          }, 1000);
        } else {
          setLoading(false);
        }
      }
    };

    fetchMenus();

    return () => controller.abort();
  }, []);

  useEffect(() => { setMobileOpen(false); setActiveDropdown(null); }, [location]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = e => { if (navRef.current && !navRef.current.contains(e.target)) setActiveDropdown(null); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <>
      <div className="topstrip">
        <div className="container topstrip-inner">
          <span>🏛 Govt. of Odisha &nbsp;|&nbsp; Dept. of Health & Family Welfare</span>

          {/* Language dropdown: between the department text and the Home link */}
          <div className="topstrip-lang">
            <LanguageSwitch lang={lang} onChange={handleLanguage} />
          </div>

          <div className="topstrip-links">
            <Link to="/">Home</Link>
            <span className="divider">|</span>
            <Link to="/site-map">Site map</Link>
            <span className="divider">|</span>
            <Link to="/web-link">Links</Link>
            <span className="divider">|</span>
            <Link to="/contact">Contact Us</Link>
          </div>
        </div>
      </div>

      <header className="brand-header">
        <div className="container brand-inner">
          <div className="brand-logo">
            <Link to="/">
              <img src={logo} alt="MKCG Logo" />
            </Link>
          </div>
          <div className="brand-text">
            <h1 className="brand-name">
              M.K.C.G. Medical College, Brahmapur, Odisha, India
            </h1>
            {/* translate="no": this line is already Odia, so Google leaves it alone */}
            <h2 className="brand-name-odia" translate="no">
              ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର
            </h2>
            <p className="brand-govt">
              (GOVT OF ODISHA)
            </p>
          </div>
        </div>

        <div className="msg_sub_sec">
          <Link to="/StudentRegistration">
            <p className="registration">
              <img src={registration} alt="registration" />
              New Registration
            </p>
          </Link>

          <Link to="/StudentLogin">
            <p className="Student-Login">
              <img src={studentlogin} alt="studentlogin" />
              Student-Login
            </p>
          </Link>
        </div>
      </header>

      <nav ref={navRef} className={`main-nav${scrolled ? " sticky" : ""}`}>
        <div className="container nav-inner">
          <button className="hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Toggle menu">
            <span className={mobileOpen ? "open" : ""} />
            <span className={mobileOpen ? "open" : ""} />
            <span className={mobileOpen ? "open" : ""} />
          </button>

          {loading ? <NavSkeleton /> : (
            <ul className="nav-list desktop-nav" style={{ marginBottom: 0 }}>
              {visibleMenus.map((menu, i) => (
                <NavItem
                  key={menu.page_id}
                  menu={menu}
                  isActive={activeDropdown === i}
                  onEnter={() => menu.children?.length && setActiveDropdown(i)}
                  onLeave={() => setActiveDropdown(null)}
                  alignRight={false}
                />
              ))}

              {overflowMenus.length > 0 && (
                <MoreDropdown
                  items={overflowMenus}
                  isActive={activeDropdown === "more"}
                  onEnter={() => setActiveDropdown("more")}
                  onLeave={() => setActiveDropdown(null)}
                />
              )}
            </ul>
          )}

          {/* Always in the DOM (hidden when closed) so Google can translate it */}
          {!loading && (
            <div className="mobile-drawer" style={{ display: mobileOpen ? "block" : "none" }}>
              <div className="mobile-lang">
                <LanguageSwitch lang={lang} onChange={handleLanguage} />
              </div>
              <MobileMenu items={menuTree} />
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
